import type { EventMember, SfdcUser } from '../../event-manager-client';
import { getInitials, getShortName, isClayHost, computeSeatPositions, getOvalDimensions } from './utils';

interface OvalDiagramProps {
  capacity: number;
  members: EventMember[];
  sfdcUsers: SfdcUser[];
  effectiveAssignments: Map<number, { tableId: number | null; seatPosition: number | null }>;
  tableId: number;
  tableName: string;
}

export function OvalDiagram({ capacity, members, sfdcUsers, effectiveAssignments, tableId, tableName }: OvalDiagramProps) {
  const dim = getOvalDimensions(capacity);
  const positions = computeSeatPositions(capacity);

  // Build seat map: seatPosition → member
  const seatMap = new Map<number, EventMember>();
  for (const m of members) {
    const assignment = effectiveAssignments.get(m.id);
    if (assignment?.tableId === tableId && assignment.seatPosition != null) {
      seatMap.set(assignment.seatPosition, m);
    }
  }

  const fontSize = dim.dotSize <= 26 ? 9 : 10;

  return (
    <div className="flex justify-center px-4 pt-4 pb-2">
      <div
        className="relative"
        style={{ width: dim.containerW, height: dim.containerH }}
      >
        {/* Inner oval table shape */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[999px] bg-nightshade-50 dark:bg-nightshade-950 border-2 border-nightshade-200 dark:border-nightshade-700"
          style={{ width: dim.innerRx * 2, height: dim.innerRy * 2 }}
        >
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] font-semibold text-nightshade-400 dark:text-nightshade-600 uppercase tracking-wider whitespace-nowrap">
            {tableName}
          </span>
        </div>

        {/* Seat dots */}
        {positions.map((pos, i) => {
          const member = seatMap.get(i);
          const isTop = pos.yPct < 45;
          const isClay = member ? isClayHost(member, sfdcUsers) : false;

          return (
            <div key={i}>
              {/* Dot */}
              <div
                className={`absolute rounded-full flex items-center justify-center font-bold -translate-x-1/2 -translate-y-1/2 transition-transform hover:scale-110 ${
                  member
                    ? isClay
                      ? 'bg-blueberry-200 dark:bg-blueberry-900/30 border-2 border-blueberry-500 shadow-[0_0_8px_rgba(99,102,241,0.3)]'
                      : 'bg-nightshade-100 dark:bg-nightshade-800 border-2 border-blueberry-500'
                    : 'bg-nightshade-50 dark:bg-nightshade-900 border-2 border-dashed border-nightshade-300 dark:border-nightshade-700'
                }`}
                style={{
                  top: `${pos.yPct}%`,
                  left: `${pos.xPct}%`,
                  width: dim.dotSize,
                  height: dim.dotSize,
                  fontSize,
                  zIndex: 1,
                }}
              >
                {member && (
                  <span className={isClay ? 'text-blueberry-700 dark:text-blueberry-300' : 'text-blueberry-600 dark:text-blueberry-400'}>
                    {getInitials(member.contact_name)}
                  </span>
                )}
              </div>

              {/* Name label */}
              {member && (
                <span
                  className="absolute text-[9px] text-nightshade-500 dark:text-nightshade-400 font-medium whitespace-nowrap -translate-x-1/2 pointer-events-none"
                  style={{
                    left: `${pos.xPct}%`,
                    ...(isTop
                      ? { top: `${pos.yPct}%`, marginTop: -(dim.dotSize / 2 + 14) }
                      : { top: `${pos.yPct}%`, marginTop: dim.dotSize / 2 + 4 }),
                    zIndex: 1,
                  }}
                >
                  {getShortName(member.contact_name)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
